// The Productivity Chart x axis: where its ticks are placed, and what each one says.
//
// Both questions are answered from the Production Groups on the axis rather than from the calendar, so
// a tick lands on a Production Group's own start and its label counts weeks and days from there. The
// two halves are deliberately in one module: a tick placed by one rule and labelled by another is
// how an axis ends up numbered in two vocabularies at once.

import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import type { ProductivityProductionGroup } from '@/types/SectionLiveKpi';
import { dayInPeriod, weekInPeriod } from './periodPosition';
import { DAY_MS, HOUR_MS, productivityDate, WEEK_MS } from './productivityFormatting';

// Label ladder thresholds, by the axis's currently visible span: wider than WIDE_THRESHOLD_MS a
// label reads week-in-period, wider than TIGHT_THRESHOLD_MS (but not wide) it reads day-in-period,
// tighter still it reads time-of-day - the same rungs the running Production Group's week/day-in-period
// chip already uses, extended one rung further for hourly detail. What a label *says*; how far
// apart labels sit is TICK_STRIDES_MS's job below.
const WIDE_THRESHOLD_MS = 60 * DAY_MS;
const TIGHT_THRESHOLD_MS = 3 * DAY_MS;

// The most ticks the axis places across its whole visible span. One tick per rung is far too many -
// a seven-week Production Group would ask for ~50 day labels, which collide into an unreadable smear at
// any realistic panel width - so the walk strides over several rungs at a time.
const MAX_TICKS = 9;

// The strides the walk is allowed to take, smallest first: hours up to a day, then whole weeks.
// Deliberately not "whatever multiple of a day makes the count fit" - that lands on strides like
// six days, which put a label on a different weekday each time and make a chart of pig weeks read
// as an arbitrary grid. Every stride here is either a divisor of a day or a whole number of weeks,
// so consecutive labels are always the same length of time apart and always a familiar one.
const TICK_STRIDES_MS = [HOUR_MS, 2 * HOUR_MS, 3 * HOUR_MS, 6 * HOUR_MS, 12 * HOUR_MS, DAY_MS, WEEK_MS, 2 * WEEK_MS, 4 * WEEK_MS, 8 * WEEK_MS, 13 * WEEK_MS, 26 * WEEK_MS, 52 * WEEK_MS];

export function tickStrideMsFor(spanMs: number): number {
    return TICK_STRIDES_MS.find((stride) => spanMs / stride <= MAX_TICKS) ?? TICK_STRIDES_MS[TICK_STRIDES_MS.length - 1];
}

// A productionGroup's exclusive upper bound, in ms: the day after a closed Production Group's `end` (whose
// `end` day is itself inclusive), or `nowMs` for a still-open one. Kept in one place so every axis,
// tick, band and line-break agrees on exactly where a Production Group stops.
export function productionGroupEndMs(productionGroup: ProductivityProductionGroup, nowMs: number): number {
    return productionGroup.end !== null ? Date.parse(productionGroup.end) + DAY_MS : nowMs;
}

// The Production Group a timestamp falls inside, if any. Production Groups for one Section never
// overlap (the backend's window-admission rule resolves them from one Section's own non-overlapping
// batches), so at most one can match.
//
// Strictly about where the Section's own data is - line breaks, event placement, band extents. How
// far the *axis* goes on counting a Production Group's weeks and days is a separate question, answered
// by labellingProductionGroup below, and the two deliberately differ under a comparison projection.
export function productionGroupContaining(productionGroups: ProductivityProductionGroup[], timestamp: number, nowMs: number): ProductivityProductionGroup | undefined {
    return productionGroups.find((productionGroup) => {
        const start = Date.parse(productionGroup.start);
        return timestamp >= start && timestamp < productionGroupEndMs(productionGroup, nowMs);
    });
}

/**
 * A comparison reference currently projected onto the axis. The projection places day N of the
 * reference on day N of the Production Group it was picked against, so past that period's own end the
 * axis is still measuring the same period - just further into it than the Section has yet reached.
 */
export type ComparisonProjection = {
    // The Production Group the reference is projected onto, by id.
    productionGroupId: number;
    // Exclusive upper bound of the projected reference - see projectedComparisonEndMs.
    endMs: number;
};

// How far a Production Group's own day/week-in-period numbering reaches on the axis: its own end,
// extended to the projection's end for the one period a reference is projected onto.
function labelledEndMs(productionGroup: ProductivityProductionGroup, nowMs: number, projection: ComparisonProjection | null): number {
    const end = productionGroupEndMs(productionGroup, nowMs);
    return projection !== null && projection.productionGroupId === productionGroup.production_group_id ? Math.max(end, projection.endMs) : end;
}

/**
 * The Production Group whose own numbering a tick belongs to. Like productionGroupContaining, except that
 * under a projection the period the reference was picked against goes on counting out to the
 * reference's last projected day.
 *
 * Without that, every tick in the reference-only stretch falls to the bare-date fallback and one
 * axis ends up labelled in two languages at once - "Uge 11" beside "27.08.2026" - which reads as
 * though the two halves measured different things when they measure the same period. Used by the
 * tick walk and the label formatter both, so a tick is never placed under one rule and labelled
 * under another.
 */
export function labellingProductionGroup(
    productionGroups: ProductivityProductionGroup[],
    timestamp: number,
    nowMs: number,
    projection: ComparisonProjection | null
): ProductivityProductionGroup | undefined {
    return productionGroups.find((productionGroup) => timestamp >= Date.parse(productionGroup.start) && timestamp < labelledEndMs(productionGroup, nowMs, projection));
}

// The `Uge X · Dag Y` pair for a Production Group on this axis, counted from its own start by the
// shared evaluator (periodPosition.ts). These wrappers exist so this module's callers can pass the
// Production Group they already hold rather than unpacking its start themselves.
//
// Frame: both operands are already on the Directory's wall clock with no offset attached and are
// stamped as UTC (see productivityInstant) - a tick's instant because that is how this axis carries
// every instant on it, and `start` because it is a bare `yyyy-MM-dd` date, which parses to UTC
// midnight. So the UTC calendar date of each IS the Directory's calendar date, which is the frame
// the day numbering has to be in to agree with the figures the server published.
function weekOf(productionGroup: ProductivityProductionGroup, timestamp: number): number {
    return weekInPeriod(Date.parse(productionGroup.start), timestamp);
}

function dayOf(productionGroup: ProductivityProductionGroup, timestamp: number): number {
    return dayInPeriod(Date.parse(productionGroup.start), timestamp);
}

/**
 * Where a point sits in its Production Group's own life, in the vocabulary the x axis beneath it is
 * already numbered in - week-in-period, day-in-period, and at hourly the time of day, which is the
 * one granularity whose time carries information. The calendar date is not repeated here: it is
 * stated in full beside this, so a chip carrying it too would name the same instant twice.
 *
 * Null for an instant no Production Group covers, which a range wider than the current period routinely
 * puts the cursor on - a Section standing empty between two batches has no day-in-period to be on.
 * The caller states the bare date alone there.
 */
export function periodPositionLabel(
    productionGroups: ProductivityProductionGroup[],
    timestamp: number,
    nowMs: number,
    granularity: ProductivityGranularity,
    projection: ComparisonProjection | null = null
): string | null {
    const productionGroup = labellingProductionGroup(productionGroups, timestamp, nowMs, projection);
    if (!productionGroup) return null;
    const week = weekOf(productionGroup, timestamp);
    const day = dayOf(productionGroup, timestamp);
    // At hourly the day alone would name a point twenty-four of them share, so the clock replaces the
    // week - which is the coarser of the two and the one still readable from the axis.
    return granularity === 'hour'
        ? (i18n.global.t('views.monitoring.activeGroups.texts.dayClock', { day, clock: productivityDate(timestamp, 'clock') }) as string)
        : (i18n.global.t('views.monitoring.activeGroups.texts.weekDay', { week, day }) as string);
}

// x-axis tick positions: one tick per stride the currently visible span calls for (see
// tickStrideMsFor), restarting at each productionGroup's own start so day/week-in-period numbering counts
// from where the Production Group began rather than from the window's edge.
export function productivityTickPositioner(productionGroups: ProductivityProductionGroup[], nowMs: number, projection: ComparisonProjection | null = null) {
    return function tickPositioner(this: { min: number; max: number }): number[] {
        const stride = tickStrideMsFor(this.max - this.min);
        const positions: number[] = [];
        for (const productionGroup of productionGroups) {
            const start = Date.parse(productionGroup.start);
            // Out to labelledEndMs, not the Production Group's own end: the projected stretch is
            // labelled in this period's weeks and days, so its ticks have to be placed by the same
            // walk that anchors on this period's start. Left to the gap walk below they would be
            // anchored on the window's edge instead, and a week label would land mid-week.
            const upper = Math.min(labelledEndMs(productionGroup, nowMs, projection), this.max);
            // The walk starts at whichever comes later, the productionGroup's own start or the left edge of
            // the visible window - rather than starting at the Production Group's start and discarding
            // everything left of the edge. Discarding loses the leftmost label outright whenever the
            // edge sits a hair inside the Production Group (which it does after any re-render that nudges the
            // extremes), leaving the axis unlabelled exactly where the reader looks first.
            for (let tick = Math.max(start, this.min); tick <= upper; tick += stride) {
                positions.push(tick);
            }
        }

        // Gaps between Production Groups get calendar-date ticks of their own (the label formatter's
        // own fallback for a tick no Production Group contains). Left bare, a range whose Production Groups
        // occupy a fraction of it - a twelve-month view of one finished period, say - renders most
        // of its axis with no marks at all, and the reader has nothing to place the empty stretch
        // against. Anything landing within half a stride of a period's own tick is dropped, so the
        // in-period numbering never gets crowded by a date beside it. A full stride, not half:
        // the two walks are anchored differently (one on the window's edge, one on each period's
        // own start), so a smaller exclusion lets them interleave and doubles the label count the
        // stride was chosen to cap.
        for (let tick = this.min; tick <= this.max; tick += stride) {
            const inGap = !labellingProductionGroup(productionGroups, tick, nowMs, projection);
            const clear = !positions.some((placed) => Math.abs(placed - tick) < stride);
            if (inGap && clear) positions.push(tick);
        }

        // Final spacing pass. The walks above are anchored independently - one per Production Group,
        // one on the window - so two of them can still land close enough together to overprint each
        // other's labels, which is exactly what a reader sees as a broken axis. Where that happens
        // the Production Group's own start wins the slot: "Uge 1" is the label worth keeping.
        const productionGroupStarts = new Set(productionGroups.map((productionGroup) => Date.parse(productionGroup.start)));
        const spaced: number[] = [];
        for (const tick of positions.sort((a, b) => a - b)) {
            const previous = spaced[spaced.length - 1];
            if (previous === undefined || tick - previous >= stride * 0.6) {
                spaced.push(tick);
            } else if (productionGroupStarts.has(tick) && !productionGroupStarts.has(previous)) {
                spaced[spaced.length - 1] = tick;
            }
        }
        return spaced;
    };
}

// x-axis label formatter: every tick productivityTickPositioner places falls inside a
// Production Group by construction, so this always resolves one; the bare-date fallback only guards a tick
// reached some other way (e.g. Highcharts' own axis-extremes ticks). The label ladder follows the
// same visible-span thresholds as the tick interval: wide shows week-in-period, mid shows
// day-in-period, tight shows time of day - `this.axis` carries the axis's current extremes, which
// is the visible range regardless of how much data is actually loaded either side of it.
export function productivityAxisLabelFormatter(productionGroups: ProductivityProductionGroup[], nowMs: number, projection: ComparisonProjection | null = null) {
    return function axisLabelFormatter(this: { value: number | string; axis: { min: number; max: number } }): string {
        const timestamp = Number(this.value);
        const productionGroup = labellingProductionGroup(productionGroups, timestamp, nowMs, projection);
        if (!productionGroup) {
            return productivityDate(timestamp, 'middle');
        }
        const span = this.axis.max - this.axis.min;
        if (span > WIDE_THRESHOLD_MS) {
            return i18n.global.t('views.monitoring.activeGroups.texts.weekWithNumber', { week: weekOf(productionGroup, timestamp) }) as string;
        }
        if (span > TIGHT_THRESHOLD_MS) {
            return i18n.global.t('views.monitoring.activeGroups.texts.dayWithNumber', { day: dayOf(productionGroup, timestamp) }) as string;
        }
        return productivityDate(timestamp, 'clock');
    };
}
